/**
 * A deliberately small ICU MessageFormat subset:
 *   "Hello {name}"                                   → interpolation
 *   "{count, plural, one {# player} other {# players}}" → plurals via Intl.PluralRules
 *   "{kind, select, player {You} other {They}}"      → select
 * Nested braces inside plural/select branches are supported one level deep (enough for UI copy).
 */
export type MessageParams = Record<string, string | number>;

const pluralCache = new Map<string, Intl.PluralRules>();

function pluralRules(locale: string): Intl.PluralRules {
  let pr = pluralCache.get(locale);
  if (!pr) {
    try {
      pr = new Intl.PluralRules(locale);
    } catch {
      pr = new Intl.PluralRules("en");
    }
    pluralCache.set(locale, pr);
  }
  return pr;
}

/** Split "one {# player} other {# players}" into { one: "# player", other: "# players" }. */
function parseBranches(src: string): Record<string, string> {
  const out: Record<string, string> = {};
  let i = 0;
  while (i < src.length) {
    while (i < src.length && /\s/.test(src[i]!)) i++;
    let key = "";
    while (i < src.length && src[i] !== "{" && !/\s/.test(src[i]!)) key += src[i++];
    while (i < src.length && /\s/.test(src[i]!)) i++;
    if (src[i] !== "{") break;
    let depth = 0;
    let j = i;
    for (; j < src.length; j++) {
      if (src[j] === "{") depth++;
      else if (src[j] === "}") {
        depth--;
        if (depth === 0) break;
      }
    }
    out[key] = src.slice(i + 1, j);
    i = j + 1;
  }
  return out;
}

export function formatMessage(locale: string, message: string, params: MessageParams = {}): string {
  let out = "";
  let i = 0;
  while (i < message.length) {
    const ch = message[i]!;
    if (ch !== "{") {
      out += ch;
      i++;
      continue;
    }
    // Find the matching close brace.
    let depth = 0;
    let j = i;
    for (; j < message.length; j++) {
      if (message[j] === "{") depth++;
      else if (message[j] === "}") {
        depth--;
        if (depth === 0) break;
      }
    }
    const inner = message.slice(i + 1, j);
    i = j + 1;
    const firstComma = inner.indexOf(",");
    if (firstComma === -1) {
      const v = params[inner.trim()];
      out += v === undefined ? `{${inner}}` : String(v);
      continue;
    }
    const name = inner.slice(0, firstComma).trim();
    const rest = inner.slice(firstComma + 1);
    const secondComma = rest.indexOf(",");
    const kind = (secondComma === -1 ? rest : rest.slice(0, secondComma)).trim();
    const branchesSrc = secondComma === -1 ? "" : rest.slice(secondComma + 1);
    const branches = parseBranches(branchesSrc);
    const value = params[name];
    if (kind === "plural") {
      const n = typeof value === "number" ? value : Number(value ?? 0);
      const exact = branches[`=${n}`];
      const category = pluralRules(locale).select(n);
      const branch = exact ?? branches[category] ?? branches.other ?? "";
      out += formatMessage(locale, branch.replaceAll("#", String(n)), params);
    } else if (kind === "select") {
      const branch = branches[String(value)] ?? branches.other ?? "";
      out += formatMessage(locale, branch, params);
    } else {
      out += value === undefined ? "" : String(value);
    }
  }
  return out;
}

export function formatDuration(locale: string, ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const two = new Intl.NumberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false });
  return `${new Intl.NumberFormat(locale, { useGrouping: false }).format(m)}:${two.format(s)}`;
}

export function formatDateTime(locale: string, ts: number): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(ts));
  } catch {
    return new Date(ts).toISOString();
  }
}

export function formatNumber(locale: string, n: number): string {
  try {
    return new Intl.NumberFormat(locale).format(n);
  } catch {
    return String(n);
  }
}

export function formatPercent(locale: string, fraction: number): string {
  try {
    return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(fraction);
  } catch {
    return `${Math.round(fraction * 100)}%`;
  }
}

export function formatList(locale: string, items: string[]): string {
  try {
    return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(items);
  } catch {
    return items.join(", ");
  }
}
